import { isBeforeDate } from '@shared/helpers/date.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import type { Project } from '@shared/models/project.model';
import { fetchProject } from './project.store';

/**
 * Changes save that can be replayed onto a newer server row.
 * Structural slice of the changes branch of {@link ProjectUpdate} so this
 * module does not import the project hook.
 */
type ChangesRevisionUpdate = {
  kind: 'changes';
  baseline: Project;
  replayOfRace?: boolean;
};

let revisionUnverified = false;

/**
 * The open client's optimistic-lock token may be behind the server.
 * Set when the document hides or the project socket drops, and cleared once
 * a changes save has checked the server row.
 */
export const markOpenProjectRevisionUnverified = (): void => {
  revisionUnverified = true;
};

/** Forget a stale-revision mark. Tests use this between cases. */
export const resetOpenProjectRevisionSyncForTests = (): void => {
  revisionUnverified = false;
};

/**
 * Mark the open project revision unverified while the tab is hidden.
 * A remote save during that time does not reach a suspended socket, and the
 * next local save would otherwise PUT the pre-hide `updated_at`.
 *
 * A document that is already hidden when the listener attaches never emits
 * `visibilitychange` for that state, so the mark is set immediately too.
 */
export const subscribeOpenProjectRevisionStaleOnHide = (): (() => void) => {
  if (typeof document === 'undefined') return () => undefined;

  const onVisibility = (): void => {
    if (document.visibilityState !== 'hidden') return;
    markOpenProjectRevisionUnverified();
  };

  if (document.visibilityState === 'hidden')
    markOpenProjectRevisionUnverified();
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
  };
};

/**
 * True when a socket drop should invalidate the local lock token.
 * Client-initiated disconnects (project navigation) already reload the row.
 */
export const projectSocketDropInvalidatesRevision = (reason: string): boolean =>
  reason !== 'io client disconnect';

/**
 * `current` keeps {@link update} and must be applied before the caller's first
 * await. Design snapshot persist releases the next save at that await, and the
 * optimistic project has to be in the store already (DEV-3888).
 * `pending` is a server read. Only an unverified changes save takes that path.
 */
export type RevisionBaselineResult<T extends { kind: string }> =
  { kind: 'current'; update: T } | { kind: 'pending'; update: Promise<T> };

/**
 * When the local lock token may be stale, load the server project and use it
 * as the changes baseline. {@link RevisionBaselineResult.kind} `current`
 * carries the same {@link update} reference when no rebase is needed.
 *
 * A failed fetch leaves the mark set so the next save tries again. The
 * existing 422 replay still covers that PUT. The mark is cleared only after
 * the two tokens have been compared.
 */
export const rebaseChangesBaselineIfRevisionUnverified = <
  T extends { kind: string },
>(
  update: T,
  loadProject: (id: number) => Promise<Project> = fetchProject,
): RevisionBaselineResult<T> => {
  if (!revisionUnverified) return { kind: 'current', update };
  if (!isChangesRevisionUpdate(update) || update.replayOfRace) {
    return { kind: 'current', update };
  }
  return {
    kind: 'pending',
    update: rebaseUnverifiedChangesBaseline(update, loadProject),
  };
};

const rebaseUnverifiedChangesBaseline = async <T extends ChangesRevisionUpdate>(
  update: T,
  loadProject: (id: number) => Promise<Project>,
): Promise<T> => {
  let fresh: Project;
  try {
    fresh = await loadProject(update.baseline.id);
  } catch {
    return update;
  }

  // Clear only after the tokens compare. A throw must leave the mark set
  // so the next save tries again instead of PUTting the stale token.
  const serverIsNewer = isBeforeDate(
    update.baseline.updated_at,
    fresh.updated_at,
  );
  revisionUnverified = false;
  if (!serverIsNewer) return update;
  return { ...update, baseline: fresh };
};

const isChangesRevisionUpdate = <T extends { kind: string }>(
  update: T,
): update is T & ChangesRevisionUpdate => {
  if (update.kind !== 'changes') return false;
  if (!('baseline' in update)) return false;
  const baseline: unknown = update.baseline;
  if (!guards.is.object(baseline)) return false;
  if (!guards.is.number(baseline.id)) return false;
  if (!guards.is.string(baseline.updated_at)) return false;
  return true;
};
